import { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FolderOpen, FileClock, BadgeCheck, Timer, Coins, MoreVertical, FilePlus2, FileSignature,
  Sparkles, AlertTriangle, Settings2, Search,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';

export const SERVICE_COLORS = {
  'communication-visuelle': { hex: '#2563eb', badge: 'bg-blue-50 text-blue-700' },
  'supports-imprimes': { hex: '#dc2626', badge: 'bg-red-50 text-red-600' },
  'production-video': { hex: '#7c3aed', badge: 'bg-violet-50 text-violet-700' },
  'habillage-signaletique': { hex: '#f59e0b', badge: 'bg-amber-50 text-amber-700' },
  'creation-entreprise': { hex: '#16a34a', badge: 'bg-green-50 text-green-700' },
};

export const ETAPES = [
  { key: 'brief_recu', label: 'Brief reçu', color: '#94a3b8' },
  { key: 'en_conception', label: 'En conception', color: '#1d4ed8' },
  { key: 'validation_client', label: 'Validation client', color: '#f59e0b' },
  { key: 'pret_a_livrer', label: 'Prêt à livrer', color: '#16a34a' },
];

const fmtNum = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
const fmtShort = (n) => {
  if (!n) return '0';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace('.', ',')} M`;
  if (n >= 1e3) return `${Math.round(n / 1e3)} k`;
  return fmtNum(n);
};
const fmtDelai = (d) => (d === null || d === undefined ? '—' : `${String(d).replace('.', ',')} j`);

const Chip = ({ tone = 'green', children, testid }) => {
  const tones = { green: 'bg-green-50 text-green-700', red: 'bg-red-50 text-red-600', slate: 'bg-slate-100 text-slate-600' };
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${tones[tone]}`} data-testid={testid}>{children}</span>;
};

function KpiCard({ icon: Icon, tint, value, label, chip, testid, delay }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-[0_2px_12px_rgba(15,23,42,0.06)] cs-rise" style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tint}`}><Icon className="h-5 w-5" /></div>
      <div className="mt-4 text-3xl font-extrabold text-[#0f1a3a] tracking-tight" data-testid={`${testid}-value`}>{value}</div>
      <div className="flex items-end justify-between gap-2 mt-1">
        <span className="text-sm text-slate-500">{label}</span>
        {chip}
      </div>
    </div>
  );
}

export function KpiRow({ stats }) {
  const ft = stats.facture_trend_pct || 0;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="conception-kpis">
      <KpiCard delay={0} testid="kpi-projets" icon={FolderOpen} tint="bg-indigo-50 text-indigo-600" value={stats.projets_en_cours} label="Projets en cours" chip={<Chip>+{stats.projets_ce_mois}</Chip>} />
      <KpiCard delay={60} testid="kpi-devis-attente" icon={FileClock} tint="bg-amber-50 text-amber-600" value={stats.devis_en_attente} label="Devis en attente" chip={<Chip tone="slate">{stats.devis_a_relancer} à relancer</Chip>} />
      <KpiCard delay={120} testid="kpi-signature" icon={BadgeCheck} tint="bg-green-50 text-green-600" value={`${stats.taux_signature} %`} label="Devis signés en contrat" chip={<Chip>{stats.contrats_signes} signés</Chip>} />
      <KpiCard delay={180} testid="kpi-delai" icon={Timer} tint="bg-violet-50 text-violet-600" value={fmtDelai(stats.delai_moyen)} label="Délai moyen de livraison" />
      <KpiCard delay={240} testid="kpi-facture" icon={Coins} tint="bg-rose-50 text-rose-500" value={fmtShort(stats.facture_mois)} label="FCFA facturés ce mois" chip={<Chip tone={ft >= 0 ? 'green' : 'red'}>{ft >= 0 ? '+' : ''}{ft} %</Chip>} />
    </div>
  );
}

export function DemandesParService({ stats, services }) {
  const rows = services.map((s) => ({ ...s, n: (stats.services || []).find((x) => x.id === s.id)?.demandes || 0 }));
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div className="bg-white rounded-2xl p-6 shadow-[0_2px_12px_rgba(15,23,42,0.06)]" data-testid="demandes-par-service">
      <h3 className="text-lg font-bold text-[#0f1a3a]">Demandes par service</h3>
      <p className="text-sm text-slate-500">30 derniers jours · {stats.demandes_30j} demande{stats.demandes_30j > 1 ? 's' : ''}</p>
      <div className="mt-4 space-y-3">
        {rows.map((r) => (
          <div key={r.id} className="grid grid-cols-[180px_1fr_32px] items-center gap-4" data-testid={`demandes-row-${r.id}`}>
            <span className="text-sm text-slate-800 truncate">{r.titre}</span>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${(r.n / max) * 100}%`, background: SERVICE_COLORS[r.id]?.hex || '#64748b' }} />
            </div>
            <span className="text-sm font-bold text-[#0f1a3a] text-right">{r.n}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AvancementProjets({ stats, onManage }) {
  const av = stats.avancement || {};
  const total = ETAPES.reduce((s, e) => s + (av[e.key] || 0), 0);
  return (
    <div className="bg-white rounded-2xl p-6 shadow-[0_2px_12px_rgba(15,23,42,0.06)]" data-testid="avancement-projets">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-[#0f1a3a]">Avancement des projets</h3>
          <p className="text-sm text-slate-500">Où en sont les {stats.projets_en_cours} projets en cours</p>
        </div>
        <button onClick={onManage} className="text-xs font-semibold text-blue-700 hover:text-blue-900 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-blue-50 transition-colors" data-testid="btn-gerer-projets">
          <Settings2 className="h-3.5 w-3.5" /> Gérer les projets
        </button>
      </div>
      <div className="mt-4 flex h-10 rounded-xl overflow-hidden gap-1 bg-slate-100">
        {total === 0 && <div className="flex-1 flex items-center justify-center text-xs text-slate-400">Aucun projet en cours</div>}
        {ETAPES.filter((e) => av[e.key]).map((e) => (
          <div key={e.key} className="flex items-center justify-center text-white text-sm font-bold" style={{ flex: av[e.key], background: e.color }} data-testid={`avancement-seg-${e.key}`}>{av[e.key]}</div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
        {ETAPES.map((e) => (
          <div key={e.key} data-testid={`avancement-legend-${e.key}`}>
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: e.color }} />
            <div className="text-sm font-bold text-[#0f1a3a]">{e.label}</div>
            <div className="text-xs text-slate-500">{av[e.key] || 0} projet{(av[e.key] || 0) > 1 ? 's' : ''}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700 flex items-center gap-2" data-testid="avancement-alert">
        <AlertTriangle className={`h-4 w-4 ${stats.attente_validation ? 'text-amber-500' : 'text-slate-400'}`} />
        {stats.attente_validation
          ? <span><b>{stats.attente_validation} projet{stats.attente_validation > 1 ? 's' : ''}</b> attend{stats.attente_validation > 1 ? 'ent' : ''} la validation du client depuis plus de 5 jours.</span>
          : <span>Aucun projet n'attend la validation du client depuis plus de 5 jours.</span>}
      </div>
    </div>
  );
}

export function ServiceCard({ svc, st, subtag, Icon, onDevis, onContrat, delay }) {
  const c = SERVICE_COLORS[svc.id] || { hex: '#334155', badge: 'bg-slate-100 text-slate-700' };
  const weeks = st?.weeks || [0, 0, 0, 0, 0, 0, 0];
  const max = Math.max(1, ...weeks);
  const trend = st?.trend_pct || 0;
  return (
    <div className="bg-white rounded-2xl p-6 shadow-[0_2px_12px_rgba(15,23,42,0.06)] flex flex-col relative transition-transform duration-300 hover:-translate-y-1 cs-rise" style={{ animationDelay: `${delay}ms` }} data-testid={`svc-card-${svc.id}`}>
      <div className="absolute top-5 right-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-slate-700 transition-colors" data-testid={`svc-menu-${svc.id}`}><MoreVertical className="h-4 w-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={onDevis}><FilePlus2 className="h-4 w-4 mr-2 text-blue-600" /> Faire un devis</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="w-14 h-14 rounded-2xl bg-[#0f1a3a] flex items-center justify-center"><Icon className="h-6 w-6 text-white" /></div>
      <h3 className="mt-4 text-xl font-bold text-[#0f1a3a]">{svc.titre}</h3>
      <span className={`mt-1 self-start text-xs font-bold px-3 py-1 rounded-lg ${c.badge}`}>{subtag}</span>
      <p className="mt-3 text-sm text-slate-500 leading-relaxed min-h-[44px]">{svc.description}</p>
      <div className="mt-4 grid grid-cols-3 gap-3">
        {[[st?.demandes ?? 0, 'Demandes', 'demandes'], [`${st?.conversion ?? 0} %`, 'Conversion', 'conversion'], [fmtDelai(st?.delai_moyen), 'Délai moyen', 'delai']].map(([v, l, k]) => (
          <div key={l} className="bg-slate-50 rounded-xl px-3 py-2.5" data-testid={`svc-${k}-${svc.id}`}>
            <div className="text-lg font-bold text-[#0f1a3a]">{v}</div>
            <div className="text-xs text-slate-500">{l}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between text-xs">
        <span className="text-slate-500">Demandes sur 7 semaines</span>
        <span className="font-bold text-[#0f1a3a]" data-testid={`svc-trend-${svc.id}`}>{trend >= 0 ? '+' : '−'}{Math.abs(trend)} %</span>
      </div>
      <div className="mt-2 flex items-end gap-1.5 h-12 border-b border-slate-100">
        {weeks.map((w, i) => (
          <div key={i} className="flex-1 rounded-t-md transition-[height] duration-700" title={`${w} demande(s)`} style={{ height: `${25 + (w / max) * 75}%`, background: c.hex, opacity: i === 6 ? 1 : 0.14 }} />
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between">
        <span className="text-sm text-slate-500">À partir de</span>
        <span className="text-lg font-bold text-[#0f1a3a]" data-testid={`svc-prix-${svc.id}`}>{fmtNum(svc.prix_depart)} FCFA</span>
      </div>
      <div className="mt-4">
        <button onClick={onDevis} className="w-full h-10 rounded-xl bg-blue-700 text-white text-sm font-bold hover:bg-blue-800 active:scale-[0.98] transition-[background-color,transform]" data-testid={`btn-devis-${svc.id}`}>Faire un devis</button>
      </div>
    </div>
  );
}

export function CustomProjectCard() {
  return (
    <div className="rounded-2xl p-8 bg-gradient-to-br from-[#14286b] to-[#1d3fbf] text-white flex flex-col items-center justify-center text-center min-h-[420px] cs-rise" data-testid="svc-custom-project">
      <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center"><Sparkles className="h-6 w-6" /></div>
      <h3 className="mt-5 text-xl font-bold">Un projet sur mesure ?</h3>
      <p className="mt-3 text-sm text-white/85 max-w-xs">Décrivez votre besoin, l'équipe vous répond avec une proposition adaptée.</p>
      <button
        onClick={() => toast.info("Formulaire 'projet sur mesure' — bientôt disponible")}
        className="mt-6 px-6 h-11 rounded-xl bg-white text-[#0f1a3a] font-bold hover:bg-blue-50 transition-colors"
        data-testid="btn-custom-project"
      >
        Nous contacter
      </button>
    </div>
  );
}

export function FilterBar({ filters, active, onChange, search, onSearch }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2 flex-wrap">
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => onChange(f)}
            className={`px-5 h-10 rounded-full text-sm font-bold transition-colors ${active === f ? 'bg-[#0f1a3a] text-white' : 'bg-white text-slate-500 hover:text-slate-800'}`}
            data-testid={`filter-${f}`}
          >
            {f}
          </button>
        ))}
      </div>
      <div className="relative">
        <Search className="absolute left-4 top-3 h-4 w-4 text-slate-400" />
        <input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Rechercher un service…"
          className="h-10 w-72 rounded-full bg-white pl-10 pr-4 text-sm outline-none focus:ring-2 focus:ring-blue-200"
          data-testid="input-search-service"
        />
      </div>
    </div>
  );
}

export function ProjetsDialog({ open, onOpenChange, devis, onChanged }) {
  const [saving, setSaving] = useState('');
  const projets = useMemo(() => (devis || []).filter((d) => (d.etape_projet || 'brief_recu') !== 'livre'), [devis]);

  const setEtape = async (d, etape) => {
    setSaving(d.id);
    try {
      await axios.patch(`/conception/devis/${d.id}/etape`, { etape });
      toast.success(`${d.numero} → ${etape === 'livre' ? 'Livré' : ETAPES.find((e) => e.key === etape)?.label}`);
      onChanged();
    } catch { toast.error("Impossible de changer l'étape"); }
    finally { setSaving(''); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="projets-dialog">
        <DialogHeader>
          <DialogTitle>Projets en cours</DialogTitle>
          <DialogDescription>Faites avancer chaque projet. « Livré » le retire de la liste.</DialogDescription>
        </DialogHeader>
        {projets.length === 0 && <p className="text-sm text-slate-500 py-8 text-center">Aucun projet en cours.</p>}
        <div className="space-y-2">
          {projets.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-xl px-4 py-3" data-testid={`projet-row-${d.id}`}>
              <div className="min-w-0">
                <div className="font-bold text-[#0f1a3a] text-sm">{d.numero} · {d.client_nom || '—'}</div>
                <div className="text-xs text-slate-500">{d.service_titre}</div>
              </div>
              <select
                value={d.etape_projet || 'brief_recu'}
                disabled={saving === d.id}
                onChange={(e) => setEtape(d, e.target.value)}
                className="h-9 rounded-lg border border-slate-200 px-2 text-sm bg-white"
                data-testid={`projet-etape-${d.id}`}
              >
                {ETAPES.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                <option value="livre">Livré</option>
              </select>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
